import { useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { setFullImageModal } from '@/slices/popSlice';
import ReactDOM from 'react-dom/client';
import NewsSwiper from './NewsSwiper'; // 文章內輪播

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const NewsContent = ({ content, newsId, setParagraph, title, slides = null }) => {
    const dispatch = useDispatch();
    const contentRef = useRef(null);
    const rootRef = useRef(null);

    // 替換標題字串
    const addTextWithRegex = useCallback((inputString, additionalText) => {
        const regex = /(<h2>|<h3>|<h2 class="news-title">|<h3 class="news-title">)/g;
        let index = -1;

        return inputString.replace(regex, (match) => {
            index++;
            return match.slice(0, 3) + additionalText + index + `'/>`;
        });
    }, []);

    // 處理 HTML 內容
    const processContent = useCallback(() => {
        if (!content || !contentRef.current) return;

        let newContent = content
            .replaceAll('<p>@swiper</p>', "<div id='news-swiper'></div>")
            .replaceAll('<img', '<img width="100%" class="cp" alt="illustration"')
            .replaceAll('text-align:justify;', '');

        // 將標題加上 段落跳轉用 element
        newContent = addTextWithRegex(
            newContent,
            ` class="news-title"><hr id='${`paragraph${newsId}`}`
        );

        contentRef.current.innerHTML = newContent;

        // 更新段落列表
        const allTitles = contentRef.current.querySelectorAll('.news-title');
        const titleList = Array.from(allTitles).map((title, i) => ({
            id: `paragraph${newsId}${i}`,
            content: title.textContent,
            tag: title.tagName,
        }));
        setParagraph(titleList);
    }, [content, newsId, setParagraph, addTextWithRegex]);

    // 處理圖片放大點擊事件
    const handleImage = useCallback(() => {
        if (!contentRef.current) return;

        const images = contentRef.current.querySelectorAll('figure > img, .news-content > p > img');
        images.forEach((img) => {
            const alt = img?.nextElementSibling?.childNodes[1]?.innerText || title;
            img.setAttribute('alt', alt);
            img.addEventListener('click', () => {
                dispatch(setFullImageModal(`<img src=${img.src} alt=${alt} />`));
            });
        });

        const iframes = contentRef.current.querySelectorAll('figure iframe');
        iframes.forEach((iframe) => {
            if (!iframe.title) iframe.title = 'myFrame';
        });
    }, [title, dispatch]);

    // 處理 iframely
    const processIframely = useCallback(async () => {
        const oembedElements = document.querySelectorAll('oembed[url]');
        for (const element of oembedElements) {
            if (!element.hasChildNodes() && window.iframely) {
                await sleep(50);
                window.iframely.load(element, element.getAttribute('url'));
            }
        }
    }, []);

    useEffect(() => {
        processContent();
        handleImage();
        processIframely();
    }, [content, processContent, handleImage, processIframely]);

    // 觀察氣 用於處理 NewsSwiper 的渲染
    useEffect(() => {
        if (slides && slides.length > 0) {
            const container = document.getElementById('news-swiper');

            if (container) {
                // Create the root only once
                if (!rootRef.current) {
                    rootRef.current = ReactDOM.createRoot(container);
                }

                // Render or update the content
                rootRef.current.render(<NewsSwiper slides={slides} />);
            }

            // Setup MutationObserver to listen for changes in the DOM
            const observer = new MutationObserver(() => {
                const container = document.getElementById('news-swiper');
                if (container) {
                    // Update content without creating a new root
                    rootRef.current.render(<NewsSwiper slides={slides} />);
                }
            });

            observer.observe(document.body, { childList: true, subtree: true });

            return () => {
                observer.disconnect();
            };
        }
    }, [slides]);

    // 文章內容
    return <div className="news-content mt-[25px] pb-[50px]" ref={contentRef} />;
};

export default NewsContent;
